import { type Observable, type Subscription } from 'rxjs';

export type ProductKey =
  | 'ai-tutor'
  | 'uat'
  | 'math-trainer'
  | 'student-cabinet'
  | 'curator-cabinet'
  | 'progress-test'
  | 'other'
  // Staff triage-panel submissions (singularity-reports-cabinet). The proxy
  // notifier @-mentions this product's registered responsible(s).
  | 'reports-cabinet'
  // Разложение составных продуктов: продукт отвечает «где был человек», и
  // вебинар, урок и домашка Skysmart — разные потоки в триаже.
  | 'talks'
  | 'skypro-public'
  | 'skysmart-webinar'
  | 'skysmart-lesson'
  | 'skysmart-homework';

/** Место внутри поверхности, откуда открыли форму. */
export type PlaceKey = 'shell' | 'header' | 'footer' | 'connection-check';

// Историческая форма: до разделения осей сюда клали продукт или имя оболочки.
// Прокся принимает её, пока не мигрируют все отправители.
export type SourceKey = PlaceKey | ProductKey | 'skysmart';

export interface ErrorReport {
  contentLessonId?: number | string;
  stepUuid?: string;
  stepIndex?: number;
  message: string;
  resourceUrls: string[];
  /** Original client filenames for `resourceUrls`, aligned by index. */
  resourceNames?: string[];
  category: string;
  roomHash: string;
  /** Surface-specific diagnostic context merged into the proxy clientMeta. */
  clientMeta?: Record<string, unknown>;
  /**
   * Math-trainer surface-specific context (task_id, submission_id,
   * task_student_id, code_length, last_verdict). Forwarded by the proxy into
   * `context.mathContext` so the reports-cabinet detail can render the math
   * block instead of "context not passed". Open shape so the FE can add new
   * fields without round-tripping through this interface — the admin
   * renderer reads keys defensively.
   */
  mathContext?: Record<string, unknown>;
}

export interface SuccessResponse {
  success: boolean;
}

/**
 * Per-call overrides for {@link ContentIssueApiProvider.send}. The lib's
 * default product is taken from the injected {@link ERROR_REPORT_PRODUCT_KEY}
 * token; callers that share a single root-scoped service across multiple
 * surfaces (e.g. the avatar app routing math-trainer + student-cabinet
 * through the same root `ErrorReportApiService`) override per submit here.
 *
 * `source` records the originating surface. For every native surface the
 * caller leaves it undefined and the proxy stores `source = product`. The
 * Curator Cabinet and the Skypro shell explicitly decouple the two:
 * curator keeps `source='curator-cabinet'`, while college reports opened in
 * Skypro keep `product='student-cabinet'` and set `source='skypro-public'`.
 */
export interface ErrorReportSendOptions {
  product?: ProductKey;
  source?: SourceKey;
}

export interface LegacyEndpointConfig {
  endpoints: string[];
  path: string;
  /**
   * Optional body mapper. When omitted, the lib sends the raw {@link ErrorReport}
   * as the JSON body — preserving SINGULAR-202 AI Tutor behavior. When supplied,
   * the returned object is used as the POST body. Return `null` to skip the
   * legacy call entirely (treated as success — used by avatar surfaces that
   * share a single scoped service but only want to dual-write for a specific
   * `options.product`).
   */
  mapPayload?: (report: ErrorReport, options?: ErrorReportSendOptions) => unknown | null;
  /** HTTP method for the legacy request. Defaults to `'POST'`. */
  httpMethod?: 'POST' | 'PUT';
}

/** Bridge between the lib's product-agnostic API service and a per-consumer upload pipeline. */
export interface ErrorReportUploadProvider {
  uploadFile(file: File | Blob, uploadInitiator?: string): Observable<string>;
  onUploading(cb: (event: unknown) => unknown): Subscription;
}

export abstract class ContentIssueApiProvider {
  abstract uploadFile(file: File | null, uploadInitiator?: string): Observable<{ id: number; file: string } | null>;

  abstract send(report: ErrorReport, options?: ErrorReportSendOptions): Observable<SuccessResponse>;

  abstract onUploading(cb: (uploadingEvent: unknown) => unknown): Subscription;
}

/** Backend status enum (mirrors proxy `src/error_report/model.py`). */
export type ReportStatus =
  | 'new'
  | 'seen'
  | 'in_progress'
  | 'waiting_human'
  | 'waiting_dev'
  | 'waiting_author'
  | 'answered'
  | 'resolved';

/**
 * Кто открыл обращение (proxy `error_report.initiator`): `user` — баг-репорт или вопрос, который
 * написал сам человек; `support` — исходящий диалог, который открыл оператор поддержки. Фронт
 * обращения не создаёт с этим полем: диалоги открывает агентский API прокси.
 */
export type ReportInitiator = 'user' | 'support';

/** Whitelisted student-facing fields, mirrors proxy `MyReportListItem`. */
export interface MyReportListItem {
  id: number;
  product: string;
  source: string;
  status: ReportStatus;
  category: string | null;
  messagePreview: string;
  attachmentsCount: number;
  createdAt: string;
  /** Необязательно: прокси до исходящих диалогов поле не отдаёт, и такая строка — баг-репорт. */
  initiator?: ReportInitiator;
  isSpark?: boolean;
}

export interface MyReportList {
  items: MyReportListItem[];
  nextCursor: number | null;
}

/** Whitelisted student-facing fields, mirrors proxy `MyReportDetail`. */
export interface MyReportDetail {
  id: number;
  product: string;
  source: string;
  status: ReportStatus;
  category: string | null;
  message: string;
  resourceUrls: string[];
  /** Original client filenames aligned by index with `resourceUrls` (may be shorter). */
  resourceNames?: string[];
  createdAt: string;
  closedAt: string | null;
  /** См. {@link MyReportListItem.initiator}. */
  initiator?: ReportInitiator;
  isSpark?: boolean;
}

/** Unread-report ids for the badge on the student history list (proxy `…/messages/my-unread`). */
export interface MyUnreadReports {
  reportIds: number[];
}
